/**
 * @file WelcomeOnboardingModal.tsx
 * @description Interactive onboarding guide modal for newly created TableGreet venues.
 * 
 * Provides venue operators with a clear, step-by-step roadmap to publish their digital menu:
 * 1. Adding dishes and organizing categories.
 * 2. Uploading logos, covers, and configuring brand presentation.
 * 3. Testing the live guest menu experience and downloading table QR codes.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Rocket, 
  UtensilsCrossed, 
  Palette, 
  QrCode, 
  CheckCircle2, 
  ArrowRight, 
  ExternalLink, 
  X, 
  Sparkles,
  Layers,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '../ui/GlassCard';
import { getPlatformMenuUrl } from '../../lib/utils';
import type { Venue } from '../../types';

interface WelcomeOnboardingModalProps {
  // Controls modal visibility
  isOpen: boolean;
  // Handler invoked when the user dismisses or completes the wizard
  onClose: () => void;
  // The active venue being configured
  venue: Venue | null;
  // Real-time catalog counts loaded from Supabase
  itemsCount: number | null;
  categoriesCount: number | null;
  // Completion status calculated from database records
  hasMenuSetup: boolean;
  hasThemeSetup: boolean;
  hasQrSetup: boolean;
}

export const WelcomeOnboardingModal: React.FC<WelcomeOnboardingModalProps> = ({
  isOpen,
  onClose,
  venue,
  itemsCount,
  categoriesCount,
  hasMenuSetup,
  hasThemeSetup,
  hasQrSetup,
}) => {
  const navigate = useNavigate();

  // Computes the authoritative guest-facing digital menu URL strictly adhering to TableGreet platform routing
  const locale = venue?.default_locale || 'en-gb';
  const publicMenuUrl = venue?.slug 
    ? `${getPlatformMenuUrl()}/${venue.slug}/${locale}`
    : null;

  // Calculates completion metrics for visual progress
  const completedStepsCount = [hasMenuSetup, hasThemeSetup, hasQrSetup].filter(Boolean).length;
  const progressPercent = Math.round((completedStepsCount / 3) * 100);

  // Navigates to a specific admin section and closes the wizard
  const handleNavigate = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          {/* Backdrop overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/85 backdrop-blur-md"
            onClick={onClose}
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-xl my-auto z-10"
          >
            <GlassCard 
              className="overflow-hidden border-white/10 shadow-2xl shadow-black/80 p-0 text-white bg-brand-surface/98" 
              intensity="dark"
            >
              {/* Header Section */}
              <div className="p-5 sm:p-7 border-b border-white/10 bg-gradient-to-b from-brand-primary/10 via-transparent to-transparent">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3.5">
                    {/* Glowing Rocket Icon Badge */}
                    <div className="w-12 h-12 rounded-2xl bg-brand-primary/20 border border-brand-primary/40 flex items-center justify-center text-brand-primary shrink-0 shadow-[0_0_25px_rgba(255,79,0,0.35)]">
                      <Rocket size={24} className="animate-pulse" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-brand-primary bg-brand-primary/15 px-2 py-0.5 rounded-full border border-brand-primary/30">
                          Setup Guide
                        </span>
                        <span className="text-xs text-gray-400 font-medium">
                          {completedStepsCount} of 3 completed
                        </span>
                      </div>
                      <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight mt-1">
                        Welcome to {venue?.name || 'Your Venue'}!
                      </h2>
                    </div>
                  </div>

                  {/* Close Button */}
                  <button
                    onClick={onClose}
                    className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-colors shrink-0 cursor-pointer"
                    title="Close setup guide"
                    aria-label="Close setup guide"
                  >
                    <X size={20} />
                  </button>
                </div>

                <p className="text-xs sm:text-sm text-gray-300 mt-3 leading-relaxed">
                  Your venue space is ready. Complete these 3 quick steps to launch your interactive digital menu for guests.
                </p>

                {/* Overall Progress Bar */}
                <div className="mt-4 pt-3 border-t border-white/5">
                  <div className="flex items-center justify-between text-[11px] font-bold text-gray-400 mb-1.5">
                    <span>Menu Launch Readiness</span>
                    <span className={progressPercent === 100 ? 'text-emerald-400' : 'text-brand-primary'}>
                      {progressPercent}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercent}%` }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                      className="h-full bg-gradient-to-r from-brand-primary to-orange-400 rounded-full shadow-[0_0_12px_rgba(255,79,0,0.5)]"
                    />
                  </div>
                </div>
              </div>

              {/* Step Checklist Body */}
              <div className="p-4 sm:p-6 space-y-3.5 max-h-[58vh] overflow-y-auto custom-scrollbar">
                {/* STEP 1: Add Menu Items */}
                <div 
                  className={`p-4 rounded-2xl border transition-all ${
                    hasMenuSetup
                      ? 'bg-emerald-500/5 border-emerald-500/25'
                      : 'bg-white/[0.03] border-white/10 hover:border-brand-primary/40'
                  }`}
                >
                  <div className="flex items-start gap-3.5">
                    <div 
                      className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                        hasMenuSetup
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          : 'bg-brand-primary/15 text-brand-primary border border-brand-primary/30'
                      }`}
                    >
                      {hasMenuSetup ? <CheckCircle2 size={20} /> : <UtensilsCrossed size={18} />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-bold text-white tracking-tight">
                          1. Add Your Menu Items
                        </h3>
                        {hasMenuSetup ? (
                          <span className="text-[10px] font-black uppercase text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded-full border border-emerald-500/30 shrink-0">
                            {itemsCount} {itemsCount === 1 ? 'item' : 'items'} added
                          </span>
                        ) : (
                          <span className="text-[10px] font-black uppercase text-orange-400 bg-orange-500/15 px-2 py-0.5 rounded-full border border-orange-500/30 shrink-0">
                            Required
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                        Add your food and drink offerings with pricing, photos, dietary tags, and descriptions.
                      </p>

                      <div className="flex flex-wrap items-center gap-2 mt-3 pt-2.5 border-t border-white/5">
                        <button
                          onClick={() => handleNavigate('/admin/items')}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-brand-primary hover:bg-brand-primary/90 px-3.5 py-1.5 rounded-xl shadow-md shadow-brand-primary/20 transition-all cursor-pointer"
                        >
                          <span>{hasMenuSetup ? 'Manage Items' : 'Add First Menu Item'}</span>
                          <ArrowRight size={13} />
                        </button>
                        <button
                          onClick={() => handleNavigate('/admin/categories')}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-xl transition-all cursor-pointer"
                        >
                          <Layers size={13} />
                          <span>Organize Categories</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* STEP 2: Customize Brand & Theme */}
                <div 
                  className={`p-4 rounded-2xl border transition-all ${
                    hasThemeSetup
                      ? 'bg-emerald-500/5 border-emerald-500/25'
                      : 'bg-white/[0.03] border-white/10 hover:border-brand-primary/40'
                  }`}
                >
                  <div className="flex items-start gap-3.5">
                    <div 
                      className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                        hasThemeSetup
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          : 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                      }`}
                    >
                      {hasThemeSetup ? <CheckCircle2 size={20} /> : <Palette size={18} />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-bold text-white tracking-tight">
                          2. Customize Brand & Atmosphere
                        </h3>
                        {hasThemeSetup ? (
                          <span className="text-[10px] font-black uppercase text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded-full border border-emerald-500/30 shrink-0">
                            Customized
                          </span>
                        ) : (
                          <span className="text-[10px] font-black uppercase text-gray-400 bg-white/10 px-2 py-0.5 rounded-full border border-white/10 shrink-0">
                            Recommended
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                        Upload your venue logo, cover photography, and choose color accents that reflect your interior.
                      </p>

                      <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-white/5">
                        <button
                          onClick={() => handleNavigate('/admin/theme')}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-white/10 hover:bg-white/15 border border-white/15 px-3.5 py-1.5 rounded-xl transition-all cursor-pointer"
                        >
                          <span>Theme & Branding Studio</span>
                          <ChevronRight size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* STEP 3: Preview Menu & Get QR Codes */}
                <div 
                  className={`p-4 rounded-2xl border transition-all ${
                    hasQrSetup
                      ? 'bg-emerald-500/5 border-emerald-500/25'
                      : 'bg-white/[0.03] border-white/10 hover:border-brand-primary/40'
                  }`}
                >
                  <div className="flex items-start gap-3.5">
                    <div 
                      className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                        hasQrSetup
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          : 'bg-purple-500/15 text-purple-400 border border-purple-500/30'
                      }`}
                    >
                      {hasQrSetup ? <CheckCircle2 size={20} /> : <QrCode size={18} />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-bold text-white tracking-tight">
                          3. Preview Menu & QR Codes
                        </h3>
                        {hasQrSetup ? (
                          <span className="text-[10px] font-black uppercase text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded-full border border-emerald-500/30 shrink-0">
                            Ready
                          </span>
                        ) : (
                          <span className="text-[10px] font-black uppercase text-gray-400 bg-white/10 px-2 py-0.5 rounded-full border border-white/10 shrink-0">
                            Final Step
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                        Test your guest menu on your mobile phone and download branded QR codes for your dining tables.
                      </p>

                      <div className="flex flex-wrap items-center gap-2 mt-3 pt-2.5 border-t border-white/5">
                        {publicMenuUrl && (
                          <a
                            href={publicMenuUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400 hover:text-emerald-300 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 px-3.5 py-1.5 rounded-xl transition-all cursor-pointer"
                          >
                            <span>Live Guest Preview</span>
                            <ExternalLink size={13} />
                          </a>
                        )}
                        <button
                          onClick={() => handleNavigate('/admin/qr')}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-xl transition-all cursor-pointer"
                        >
                          <QrCode size={13} />
                          <span>QR Code Studio</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 sm:p-6 border-t border-white/10 bg-white/[0.01] flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-1.5 text-[11px] text-gray-400 text-center sm:text-left">
                  <Sparkles size={13} className="text-brand-primary shrink-0" />
                  <span>The Go Live checklist stays docked in the bottom corner.</span>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-5 py-2.5 bg-white/10 hover:bg-white/15 border border-white/10 rounded-xl text-xs font-bold text-white transition-colors cursor-pointer text-center"
                >
                  Continue to Dashboard
                </button>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
